import { Button, Card, Delete, Title } from 'rbx';

import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { css } from 'emotion';
import { format } from 'date-fns';
import useUser from '../hooks/useUser';
import useSubscriptions from '../hooks/useSubscriptions';
import MagicLinkCreator from '../pages/account/components/MagicLinkCreator';
import DeleteAccount from '../pages/account/components/DeleteAccount';
import * as API from '../lib/api';
import { UserRoleTag } from './';
import { getPlanData } from '../utils';

function UserCard({ photo }) {
  const { user, setUser, updateUserField, getUserById } = useUser();
  const {
    subscription,
    isLoading: isSubscriptionDetailLoading,
  } = useSubscriptions();
  const [loading, setLoading] = useState(false);
  const [isMarkTrialLoading, setIsMarkTrialLoading] = useState(false);
  const billing = {
    1: 'Stripe',
    2: 'Spreedly',
    4: 'Paddle',
    5: 'Solid',
    6: 'FastSpring',
    7: 'Mollie',
    8: 'Revolut',
    9: 'RazorPay',
    10: 'Payu',
    11: 'Paypal',
    12: 'Dimoco',
    13: 'PayXpert',
  };

  if (!user) {
    return null;
  }

  let name = user.firstName;
  if (user.lastName) name += ` ${user.lastName}`;
  name = name || 'User';

  const primaryEmail = user.emails.find(e => e.primary);

  const clickHandler = () => setUser(null);

  async function getFreeAccessHandler() {
    if (loading) return;
    const res = window.confirm('Are you sure you want to change access?');
    if (!res) return;
    setLoading(true);
    const accessUntil = new Date();
    accessUntil.setFullYear(accessUntil.getFullYear() + 1);
    const newValue =
      user.freeAccessUntil === null ||
      user.freeAccessUntil === 'null' ||
      user?.freeAccessUntil === undefined
        ? accessUntil
        : null;
    await updateUserField(user.id, 'freeAccessUntil', newValue);
    await getUserById(user.id);
    setLoading(false);
  }

  async function markTrialUsedHandler(nextHasUsedTrial) {
    if (isMarkTrialLoading) return;
    const actionText = nextHasUsedTrial ? 'enable' : 'disable';
    const res = window.confirm(
      `Are you sure you want to ${actionText} trial used for this user?`,
    );
    if (!res) return;
    setIsMarkTrialLoading(true);
    try {
      await API.markUsedTrial(user.id, nextHasUsedTrial);
      await getUserById(user.id);
      window.alert('Updated successfully');
    } catch (e) {
      window.alert('Not able to mark');
    } finally {
      setIsMarkTrialLoading(false);
    }
  }

  const verifyBillingVersion = billingVersion => {
    const numbers = [9];
    if (numbers.includes(billingVersion)) {
      return true;
    }
    return false;
  };

  const getPlan = (billingCycle, isTrial) => {
    if (billingCycle instanceof Object) {
      billingCycle = billingCycle.value;
    }
    if (isTrial) {
      if (billingCycle === 365) {
        return {
          billingCycle: {
            value: 365,
          },
          trialDays: 30,
        };
      } else {
        return {
          billingCycle: {
            value: 30,
          },
          trialDays: 14,
        };
      }
    } else {
      return {
        billingCycle: {
          value: billingCycle,
        },
        trialDays: 0,
      };
    }
  };

  return (
    <Card
      className={css`
        position: sticky;
        top: 20px;
      `}
    >
      <Card.Header>
        <Card.Header.Title>Current user</Card.Header.Title>
        <Card.Header.Icon onClick={clickHandler}>
          <Delete />
        </Card.Header.Icon>
      </Card.Header>
      {photo && user.photo ? (
        <Card.Image>
          <img
            className={css`
              display: inline-block;
              width: 100px;
              height: 100px;
              border-radius: 50%;
              background-repeat: no-repeat;
              background-position: center center;
              background-size: cover;
              margin: 10px 10px;
            `}
            src={user.photo}
            alt="user avatar"
          />
        </Card.Image>
      ) : null}
      <Card.Content
        className={css`
          padding: 0.5em 1em 0.001em 1em;
        `}
      >
        <Title size={5}>{name}</Title>
        {primaryEmail ? (
          <div>
            <b>Email:</b> {primaryEmail.email}
            <br />
            <b>Email verification status:</b> {primaryEmail.verificationStatus}
          </div>
        ) : null}
        {!!user?.createdAt && (
          <p>
            <time title={user?.createdAt}>
              <b>User since: </b>{' '}
              {format(new Date(user?.createdAt), 'do MMM yyyy')}
            </time>
          </p>
        )}
        <p>
          <b>Domain:</b> {user.domain}
        </p>
        {!!user?.billingVersion && (
          <p>
            <b>Billing Service:</b> {billing[user.billingVersion]}
          </p>
        )}
        {!!user?.freeAccessUntil && (
          <p>
            Free access until:{' '}
            {format(new Date(user?.freeAccessUntil), 'do MMM yyyy')}
          </p>
        )}
        {!isSubscriptionDetailLoading &&
          (!!subscription?.cardDetails?.cardBrand || !!user?.cardBrand) && (
            <p>
              <b>Card Brand:</b>{' '}
              {user?.cardBrand || subscription?.cardDetails?.cardBrand}
            </p>
          )}
        {/* {!isSubscriptionDetailLoading &&
          !!subscription?.cardDetails?.cardLastFour && (
            <p>
              <b>Card Num: </b> **** **** ****{' '}
              {subscription?.cardDetails?.cardLastFour}
            </p>
          )} */}
        {(!!user?.cardCountry || !!subscription?.cardDetails?.country) && (
          <p>
            <b>Card Country: </b>
            {user?.cardCountry || subscription?.cardDetails?.country}
          </p>
        )}
        {!isSubscriptionDetailLoading && subscription?.planName && (
          <p>
            <b>Subscription Plan:</b>{' '}
            {subscription?.planId && verifyBillingVersion(user.billingVersion)
              ? getPlanData({
                  plan: getPlan(
                    subscription?.billingCycle,
                    subscription?.isTrialPlan,
                  ),
                }).productName
              : subscription?.planName}
          </p>
        )}
        {!isSubscriptionDetailLoading && subscription?.billingCycle && (
          <p>
            <b>Subscription Frequency:</b>{' '}
            {`Renews every ${
              subscription?.planId && verifyBillingVersion(user.billingVersion)
                ? getPlanData({
                    plan: getPlan(
                      subscription?.billingCycle,
                      subscription?.isTrialPlan,
                    ),
                  }).intervalDays
                : subscription?.billingCycle
            } days`}
          </p>
        )}
        {!isSubscriptionDetailLoading && subscription?.amount && (
          <p>
            <b>Subscription Amount:</b>{' '}
            {`${subscription?.amount} ${subscription?.currencyCode}`}
          </p>
        )}
        <p>
          <UserRoleTag role={user.role} />
        </p>
        <br></br>
        <div>
          <div
            className={css`
              margin-bottom: 0.75rem;
            `}
          >
            <div
              className={css`
                display: flex;
                align-items: center;
                gap: 0.75rem;
              `}
            >
              <label
                htmlFor="mark-trial-used-toggle"
                className={css`
                  position: relative;
                  display: inline-block;
                  width: 56px;
                  height: 30px;
                  cursor: ${isMarkTrialLoading ? 'not-allowed' : 'pointer'};
                  opacity: ${isMarkTrialLoading ? 0.6 : 1};
                `}
              >
                <input
                  id="mark-trial-used-toggle"
                  type="checkbox"
                  checked={!!user?.hasUsedTrial}
                  disabled={isMarkTrialLoading}
                  onChange={e => markTrialUsedHandler(e.target.checked)}
                  className={css`
                    opacity: 0;
                    width: 0;
                    height: 0;
                  `}
                />
                <span
                  className={css`
                    position: absolute;
                    inset: 0;
                    background-color: ${user?.hasUsedTrial
                      ? '#3498db'
                      : '#cfd3d8'};
                    border-radius: 999px;
                    transition: 0.2s;

                    &::before {
                      content: '';
                      position: absolute;
                      height: 22px;
                      width: 22px;
                      left: ${user?.hasUsedTrial ? '30px' : '4px'};
                      top: 4px;
                      background-color: #f1f3f5;
                      border-radius: 50%;
                      transition: 0.2s;
                    }
                  `}
                />
              </label>
              <span>
                {user?.hasUsedTrial
                  ? 'Trial already marked as used'
                  : 'Trial not marked as used'}
              </span>
            </div>
          </div>
          <Button
            color="info"
            disabled={loading}
            onClick={getFreeAccessHandler}
          >
            {user.freeAccessUntil === null ||
            user.freeAccessUntil === 'null' ||
            user?.freeAccessUntil === undefined
              ? 'Grant One Year Free Access'
              : 'Cancel One Year Free Access'}
          </Button>
          <div
            className={css`
              margin-top: 0.75rem;
            `}
          >
            {/* spacing before next actions */}
          </div>
          {/* {user.subscriptionId && (
            <Column.Group>
              <Column size="two-thirds">
                <InfoPanel />
              </Column>
            </Column.Group>
          )} */}
          <MagicLinkCreator />
          <DeleteAccount />
        </div>
      </Card.Content>
    </Card>
  );
}

UserCard.propTypes = {
  photo: PropTypes.bool,
};

UserCard.defaultProps = {
  photo: true,
};

export default UserCard;
